import Vue from "vue";
import cookie from "@/utils/cookie";
import api from "@/api";
import notify from "@/utils/notify";
import getLocale from "@/utils/locale";
import VueI18n from "vue-i18n";

Vue.use(VueI18n);

const i18n = new VueI18n({
  locale: getLocale()
});

const state = () => ({
  topics: [],
  // Saving the value defined to randomize the list to avoid
  // using a new value each time the virtual dom is re-rendered
  randomValue: Math.random(),
  redirectAfterSignup: null,
  routes: [],
  theme: null,
  coupon: null,
  name: "",
  direction: "forward",
  currentPath: null,
  tinderBooks: [],
  userData: {
    "goals": [],
    "topics": [],
    "consume_types": [],
    "daily_reading_time": "",
    "books_per_year": 0,
    "app_usage_times": [],
    "books": [],
    "likedBooks": [],
    "dislikedBooks": [],
    "challengeBooks": []
  }
});

const mutations = {
  config: (state, {
    routes,
    redirectAfterSignup,
    theme,
    coupon,
    name
  }) => {
    Vue.set(state, "routes", routes);
    Vue.set(state, "redirectAfterSignup", redirectAfterSignup);
    Vue.set(state, "theme", theme);
    Vue.set(state, "coupon", coupon);
    Vue.set(state, "name", name);
  },
  direction: (state, payload) => {
    Vue.set(state, "direction", payload ? "forward" : "backward");
  },
  currentPath: (state, payload) => {
    Vue.set(state, "currentPath", payload);
  },
  userData: (state, payload) => {
    Vue.set(state, "userData", { ...state.userData, ...payload });
  },
  topics: (state, payload) => {
    Vue.set(state, "topics", payload);
  },
  setTinderBooks: (state, bookList) => {
    Vue.set(state, "tinderBooks", bookList);
  }
};

const getters = {
  routeIndexByPath: (state) => (path) => {
    return state.routes.findIndex(a => a.path === path);
  },
  topics: (state) => {
    return state.topics.sort((a, b) => b.priority - a.priority);
  },
  otherGoal: (state) => {
    return state.userData.otherGoal || [];
  },
  selectedGoals: (state) => {
    return state.userData.goals || [];
  },
  selectedTopics: (state) => {
    return state.userData.topics || [];
  },
  likedBooks: (state) => {
    return state.userData.likedBooks;
  },
  dislikedBooks: (state) => {
    return state.userData.dislikedBooks;
  },
  tinderBooks: (state) => state.tinderBooks,
  challengeBooks: (state) => state.userData.challengeBooks || [],
  recommendedBooks: (state, getters) => {
    return getters.selectedTopics.reduce((acc, value) => {
      const topic = state.topics.find(a => a.slug === value);
      const books = topic &&
        topic.books &&
        topic.books.map(a => ({ ...a, category: topic.title }))
          .filter(book => !acc.some(item => item.slug === book.slug))
           || [];

      acc = [...acc, ...books];
      return acc;
    }, []).filter(b => !state.userData.dislikedBooks.includes(b.slug));
  },
  randomizeRecommendedBooks: (state, getters) => {
    return getters.recommendedBooks.sort(() => state.randomValue - 0.5).slice(0, 10);
  },
  booksCount: (state, getters) => {
    const bookCount = getters.selectedTopics.reduce((acc, value) => {
      const topic = state.topics.find(a => a.slug === value) || { books_count: 0 }; // eslint-disable-line camelcase
      return acc + topic.books_count;
    }, 0);

    if (bookCount > 5000) {
      return 5000;
    }

    return bookCount;
  },
  categoriesCount: (state) => {
    return state.topics.length;
  },
  userDataJson: (state) => {
    const userData = { ...state.userData };

    userData.challengeBooks = state.userData.challengeBooks.map(b => b.slug);

    const snakeCaseUserData = {};

    for (const key in userData) {
      const snakeCaseKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();

      snakeCaseUserData[snakeCaseKey] = userData[key];
    }

    return JSON.stringify(snakeCaseUserData);
  },
  currentRouteIndex: (state) => {
    return state.routes.findIndex(a => a.path === state.currentPath);
  },
  nextRoute: (state, getters) => {
    const nextRoute = state.routes[getters.currentRouteIndex + 1];

    return nextRoute ? nextRoute.path : "signup";
  }
};

const actions = {
  init: async ({ commit, dispatch }, { config, name }) => {
    commit("config", {
      ...config,
      name
    });

    let storedState =  localStorage.getItem(`onboarding-state-${name}-${getLocale()}`);

    if (storedState) {
      commit("userData", JSON.parse(storedState));
      dispatch("fetchRecommendedBooks");
    }

    if (config.coupon) {
      cookie("coupon_code", config.coupon);
    }

    cookie("new_onboarding", 1);

    try {
      commit("topics", (await api.categories.withBookList()).categories);
    } catch (_) {
      notify("Failed to fetch categories");
    }
  },
  updateTopics: async({ dispatch }, topics) => {
    dispatch("updateUserData", topics);
    dispatch("fetchRecommendedBooks");
  },
  fetchRecommendedBooks: async ({ getters, commit }) => {
    const onboarding = await api.books.onboarding(getters.selectedTopics.join(","), i18n.locale, 12);

    commit("setTinderBooks", onboarding.books);
  },
  direction: ({ commit, getters }, { to, from }) => {
    const toIndex = getters.routeIndexByPath(to.path);
    const fromIndex = getters.routeIndexByPath(from.path);

    commit("direction", toIndex > fromIndex);
  },
  updateLikedBook({  state, dispatch }, { isLiked, bookSlug }) {
    const likedBooks = state.userData.likedBooks.map(b => b);
    const dislikedBooks = state.userData.dislikedBooks.map(b => b);
    const findAndRemoveBookFromList = (list, bookSlug)  => {
      let bookIndex = list.findIndex((b) => b === bookSlug);
      if (bookIndex > -1) {
        list.splice(bookIndex, 1);
      }
      return list;
    };

    if (isLiked) {
      likedBooks.push(bookSlug);
      findAndRemoveBookFromList(dislikedBooks, bookSlug);
    } else {
      dislikedBooks.push(bookSlug);
      findAndRemoveBookFromList(likedBooks, bookSlug);
    }

    dispatch("updateUserData", { likedBooks, dislikedBooks });
  },
  updateUserData({ commit, state }, userData) {
    commit("userData", userData);
    localStorage.setItem(
      `onboarding-state-${state.name}-${getLocale()}`,
      JSON.stringify({ ...state.userData, ...userData })
    );
  },
  populateChallengeBooks({ dispatch, getters }) {
    if (getters.challengeBooks.length > 0) return;

    const challengeBooks = getters.likedBooks.map((slug) => getters.tinderBooks.find((b) => b.slug === slug));

    const totalNeededBooks = 7 - challengeBooks.length;

    const possibleBooks = getters.tinderBooks.filter((book) => {
      return !(
        getters.likedBooks.includes(book.slug) ||
        getters.dislikedBooks.includes(book.slug)
      );
    });

    challengeBooks.push(...possibleBooks.slice(0, totalNeededBooks));

    dispatch("updateUserData", { challengeBooks });
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
